import React, { FC } from 'react'
import { CheckboxProps, makeStyles, Box, Typography, lighten, createStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { ReactComponent as ManageFleet } from 'apps/fleet-planner/components/illustrations/ManageFleet.svg'
import { ReactComponent as SearchLoads } from 'apps/fleet-planner/components/illustrations/SearchLoads.svg'
import { ReactComponent as Verified } from 'apps/fleet-planner/components/illustrations/Verified.svg'
import { CheckFilledIcon, CheckOutlineIcon } from 'transfix-icons'

const IconComponents = {
  SearchLoads,
  ManageFleet,
  Verified,
}

export type TIconName = keyof typeof IconComponents

const getIconComponentByName = (iconName: TIconName) => {
  return IconComponents[iconName]
}

type TPickedCheckboxProps = 'color' | 'disabled' | 'required' | 'value' | 'checked'

/**
 * Using the base props of <Checkbox /> since it closely resembles that component
 */
export interface IRichOptionProps extends Pick<CheckboxProps, TPickedCheckboxProps> {
  title: string
  description: string
  iconName: TIconName
  /**
   * @default 'rich-option'
   */
  dataTest?: string
  onChange?: (isChecked: boolean) => void
}

// Permitted className values for `createStyles`
type TRichOptionClassKey =
  | 'root'
  | 'checked'
  | 'iconContainer'
  | 'textContainer'
  | 'checkContainer'
  | 'title'
  | 'description'
  | 'checkIcon'

const useRichOptionStyles = makeStyles(theme =>
  createStyles<TRichOptionClassKey, Partial<IRichOptionProps>>({
    root: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      border: `1px solid ${brandColors.coolGray4}`,
      borderRadius: theme.shape.borderRadius,

      // On hover, darken the background a bit
      '&:hover': {
        backgroundColor: lighten(brandColors.coolGray1, 0.5),
        borderColor: lighten(brandColors.coolGray5, 0.5),
        cursor: 'pointer',
      },

      // On hover, darken the icon color
      '&:hover $checkIcon': {
        color: props => (props.checked ? brandColors.skyBlue7 : brandColors.coolGray5),
      },

      padding: theme.spacing(2),
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(3),
      },
    },
    // When the `root` component is checked, it has different styles
    checked: {
      borderColor: theme.palette.primary.main,
      backgroundColor: lighten(brandColors.skyBlue2, 0.5),

      '&:hover': {
        backgroundColor: lighten(brandColors.skyBlue2, 0.25),
        borderColor: brandColors.skyBlue6,
        cursor: 'pointer',
      },
    },
    iconContainer: {
      display: 'none',

      [theme.breakpoints.up('md')]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 56,
        flex: '0 0 56px',
        marginRight: theme.spacing(3),
      },
    },
    textContainer: {
      display: 'block',
      alignItems: 'center',
      flex: '1 1 auto',
      marginRight: theme.spacing(1),
    },
    checkContainer: {
      flex: '0 1 auto',
      display: 'flex',
      alignItems: 'center',
    },
    title: {
      color: brandColors.skyBlue6,
      fontWeight: theme.typography.fontWeightMedium,
      marginBottom: theme.spacing(0.5),
    },
    description: {
      color: brandColors.coolGray8,
    },
    checkIcon: {
      width: 26,
      height: 26,
      color: props => (props.checked ? brandColors.skyBlue6 : brandColors.coolGray4),
    },
  })
)

/**
 * Checkbox-like option, used to present choices in Carrier Portal onboarding
 */
export const RichOption: FC<IRichOptionProps> = ({
  checked: checkedProp = false,
  iconName: iconNameProp,
  title: titleProp = '',
  description: descriptionProp = '',
  onChange: onChangeProp = () => {},
  dataTest = 'rich-option',
}) => {
  const classes = useRichOptionStyles({ checked: checkedProp })

  const getRootClassName = (): string => {
    const classNames = [classes.root]
    if (checkedProp) classNames.push(classes.checked)
    return classNames.join(' ')
  }

  const getCheckIcon = () => (checkedProp ? CheckFilledIcon : CheckOutlineIcon)
  const CheckIcon = getCheckIcon()

  const IconComponent = getIconComponentByName(iconNameProp)

  const clickHandler = () => onChangeProp(!checkedProp)

  return (
    <Box className={getRootClassName()} onClick={clickHandler} data-test={dataTest}>
      <Box className={classes.iconContainer}>
        <IconComponent />
      </Box>
      <Box className={classes.textContainer}>
        <Typography
          variant='body1'
          component='div'
          className={classes.title}
          data-test={`${dataTest}-title`}>
          {titleProp}
        </Typography>
        <Typography
          variant='body2'
          component='div'
          className={classes.description}
          data-test={`${dataTest}-description`}>
          {descriptionProp}
        </Typography>
      </Box>
      <Box className={classes.checkContainer}>
        <CheckIcon className={classes.checkIcon} />
      </Box>
    </Box>
  )
}
